import React, { useRef, useEffect, useState } from "react";
import QRCode from "qrcode";
import { C, R, SH, F, statusStyle, numeric, monoRef } from "../theme";
import { Icon } from "../icons";
import { trackUrl } from "../utils";

// ─── Bouton ───────────────────────────────────────────────────────────────────
// NOTE : `type` n'a volontairement PAS de valeur par défaut.
// Le comportement natif d'un <button> sans type est « submit », et de nombreux
// formulaires du projet en dépendent (Login.js, MFASetup.js…). Forcer
// type="button" ici neutraliserait silencieusement leur soumission.
export function Btn({
  children, onClick, style, variant = "primary", disabled, small, type,
  icon, iconRight, fullWidth, title, loading,
}) {
  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
    border: "1px solid transparent", borderRadius: R.md,
    fontWeight: F.weight.semi,
    cursor: disabled || loading ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    padding: small ? "6px 11px" : "9px 16px",
    fontSize: small ? F.size.base : F.size.md,
    lineHeight: 1.3,
    width: fullWidth ? "100%" : undefined,
    whiteSpace: "nowrap",
  };

  const variants = {
    primary: { background: C.navy, color: C.inkOn, borderColor: C.navy },
    dark:    { background: C.navy, color: C.inkOn, borderColor: C.navy },
    light:   { background: C.navySoft, color: C.navy, borderColor: "transparent" },
    ghost:   { background: "transparent", color: C.ink, borderColor: C.borderStrong },
    white:   { background: C.surface, color: C.navy, borderColor: C.border },
    danger:  { background: C.danger, color: C.inkOn, borderColor: C.danger },
    success: { background: C.success, color: C.inkOn, borderColor: C.success },
    accent:  { background: C.amber, color: C.inkOn, borderColor: C.amber },
    subtle:  { background: "transparent", color: C.inkMuted, borderColor: "transparent" },
  };

  const hovers = {
    primary: C.navyHover, dark: C.navyHover, danger: C.dangerHover,
    success: "#166534", accent: C.amberHover,
  };

  const v = variants[variant] || variants.primary;
  const size = small ? 14 : 16;

  return (
    <button
      type={type}
      title={title}
      className="btn-bounce"
      style={{ ...base, ...v, ...style }}
      onClick={onClick}
      disabled={disabled || loading}
      onMouseEnter={e => {
        if (disabled || loading) return;
        if (hovers[variant]) e.currentTarget.style.background = hovers[variant];
        else if (variant === "ghost" || variant === "white") e.currentTarget.style.background = C.surfaceHover;
        else if (variant === "light") e.currentTarget.style.background = "#e2e8f0";
        else if (variant === "subtle") e.currentTarget.style.background = C.surfaceHover;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = (variants[variant] || variants.primary).background;
      }}
    >
      {loading
        ? <Icon name="loading" size={size} style={{ animation: "spin .7s linear infinite" }} />
        : icon && <Icon name={icon} size={size} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size} />}
    </button>
  );
}

// ─── Bouton icône seule ───────────────────────────────────────────────────────
export function IconBtn({ icon, onClick, title, variant = "subtle", size = 16, disabled, style, danger }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 30, height: 30, borderRadius: R.md,
        border: variant === "outline" ? `1px solid ${C.border}` : "1px solid transparent",
        background: "transparent",
        color: danger ? C.danger : C.inkMuted,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "background .15s ease, color .15s ease",
        ...style,
      }}
      onMouseEnter={e => {
        if (disabled) return;
        e.currentTarget.style.background = danger ? C.dangerSoft : C.surfaceHover;
        e.currentTarget.style.color = danger ? C.danger : C.ink;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = danger ? C.danger : C.inkMuted;
      }}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}

// ─── Badge de statut ──────────────────────────────────────────────────────────
export function Badge({ status, small }) {
  const s = statusStyle(status);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: R.pill,
      padding: small ? "1px 7px 1px 6px" : "2px 9px 2px 7px",
      fontSize: small ? F.size.xs : F.size.sm,
      fontWeight: F.weight.semi,
      whiteSpace: "nowrap",
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
}

// ─── Étiquette générique ──────────────────────────────────────────────────────
export function Chip({ children, tone = "neutral", icon, small }) {
  const tones = {
    neutral: { bg: C.surfaceAlt, color: C.inkMuted, border: C.border },
    navy:    { bg: C.navySoft,   color: C.navy,     border: "#cbd5e1" },
    success: { bg: C.successSoft,color: C.success,  border: C.successBorder },
    danger:  { bg: C.dangerSoft, color: C.danger,   border: C.dangerBorder },
    warning: { bg: C.warningSoft,color: C.warning,  border: C.warningBorder },
    info:    { bg: C.infoSoft,   color: C.info,     border: C.infoBorder },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: t.bg, color: t.color, border: `1px solid ${t.border}`,
      borderRadius: R.sm,
      padding: small ? "1px 6px" : "2px 8px",
      fontSize: small ? F.size.xs : F.size.sm,
      fontWeight: F.weight.medium,
      whiteSpace: "nowrap",
    }}>
      {icon && <Icon name={icon} size={small ? 11 : 12} />}
      {children}
    </span>
  );
}

// ─── Carte ────────────────────────────────────────────────────────────────────
export function Card({ children, style, onClick, hover, className = "", padded = true }) {
  return (
    <div
      className={`${hover ? "card-hover" : ""} ${className}`}
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: R.lg,
        padding: padded ? 16 : 0,
        ...style,
      }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

// ─── En-tête de section ───────────────────────────────────────────────────────
export function SectionHeader({ title, subtitle, icon, actions, style }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      gap: 12, marginBottom: 14, flexWrap: "wrap", ...style,
    }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{
          display: "flex", alignItems: "center", gap: 8,
          fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink, letterSpacing: "-.01em",
        }}>
          {icon && <Icon name={icon} size={17} color={C.inkMuted} />}
          {title}
        </h2>
        {subtitle && (
          <p style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 3 }}>{subtitle}</p>
        )}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>{actions}</div>}
    </div>
  );
}

// ─── Libellé de champ ─────────────────────────────────────────────────────────
function FieldLabel({ children, required, hint }) {
  if (!children) return null;
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: 5,
      fontWeight: F.weight.medium, marginBottom: 5,
      fontSize: F.size.base, color: C.inkBody,
    }}>
      {children}
      {required && <span style={{ color: C.danger }} aria-hidden="true">*</span>}
      {hint && <span style={{ color: C.inkFaint, fontWeight: F.weight.normal }}>· {hint}</span>}
    </label>
  );
}

// ─── Champ texte ──────────────────────────────────────────────────────────────
export function Input({
  label, value, onChange, type = "text", placeholder, required, min, max,
  onBlur, maxLength, disabled, error, hint, icon, style, inputMode, autoFocus, name,
}) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <FieldLabel required={required} hint={hint}>{label}</FieldLabel>
      <div style={{ position: "relative" }}>
        {icon && (
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.inkFaint, pointerEvents: "none" }}>
            <Icon name={icon} size={15} />
          </span>
        )}
        <input
          className="field"
          name={name}
          type={type}
          value={value ?? ""}
          onChange={e => onChange && onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          min={min}
          max={max}
          onBlur={onBlur}
          maxLength={maxLength}
          disabled={disabled}
          inputMode={inputMode}
          autoFocus={autoFocus}
          aria-invalid={error ? "true" : undefined}
          style={icon ? { paddingLeft: 32 } : undefined}
        />
      </div>
      {error && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: F.size.sm, color: C.danger }}>
          <Icon name="alert" size={12} />{error}
        </div>
      )}
    </div>
  );
}

// ─── Liste déroulante ─────────────────────────────────────────────────────────
export function Select({
  label, value, onChange, children, required, disabled, error, hint, style, name, placeholder,
}) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <FieldLabel required={required} hint={hint}>{label}</FieldLabel>
      <select
        className="field"
        name={name}
        value={value ?? ""}
        onChange={e => onChange && onChange(e.target.value)}
        required={required}
        disabled={disabled}
        aria-invalid={error ? "true" : undefined}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {children}
      </select>
      {error && (
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: F.size.sm, color: C.danger }}>
          <Icon name="alert" size={12} />{error}
        </div>
      )}
    </div>
  );
}

// ─── Zone de texte ────────────────────────────────────────────────────────────
export function Textarea({ label, value, onChange, placeholder, rows = 3, required, disabled, hint, style, maxLength }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <FieldLabel required={required} hint={hint}>{label}</FieldLabel>
      <textarea
        className="field"
        value={value ?? ""}
        onChange={e => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        required={required}
        disabled={disabled}
        maxLength={maxLength}
        style={{ resize: "vertical", minHeight: 68, lineHeight: 1.5 }}
      />
    </div>
  );
}

// ─── Interrupteur ─────────────────────────────────────────────────────────────
export function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label style={{
      display: "inline-flex", alignItems: "center", gap: 9,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .5 : 1,
      fontSize: F.size.md, color: C.inkBody,
    }}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange && onChange(!checked)}
        style={{
          width: 34, height: 20, borderRadius: R.pill, flexShrink: 0,
          background: checked ? C.navy : "#cbd5e1",
          border: "none", padding: 2, cursor: "inherit",
          transition: "background .18s ease",
          display: "flex", alignItems: "center",
        }}
      >
        <span style={{
          width: 16, height: 16, borderRadius: "50%", background: "#fff",
          transform: checked ? "translateX(14px)" : "translateX(0)",
          transition: "transform .18s ease",
          boxShadow: SH.sm,
        }} />
      </button>
      {label}
    </label>
  );
}

// ─── Indicateur de chargement ─────────────────────────────────────────────────
export function Spinner({ label = "Chargement…", inline }) {
  if (inline) return <Icon name="loading" size={15} style={{ animation: "spin .7s linear infinite" }} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 40, gap: 10 }}>
      <Icon name="loading" size={22} color={C.inkMuted} style={{ animation: "spin .7s linear infinite" }} />
      <span style={{ fontSize: F.size.base, color: C.inkFaint }}>{label}</span>
    </div>
  );
}

// ─── Squelette de chargement ──────────────────────────────────────────────────
export function Skeleton({ width = "100%", height = 14, style }) {
  return <div className="skeleton" style={{ width, height, ...style }} />;
}

export function SkeletonRows({ rows = 5, cols = 4 }) {
  return (
    <div style={{ padding: 12 }}>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} style={{ display: "flex", gap: 12, padding: "10px 0" }}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} width={c === 0 ? 90 : `${100 / cols}%`} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── État vide ────────────────────────────────────────────────────────────────
export function EmptyState({ icon = "inbox", title, description, action, compact }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      textAlign: "center", padding: compact ? "28px 20px" : "48px 24px", gap: 4,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: R.lg,
        background: C.surfaceAlt, border: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: C.inkFaint, marginBottom: 8,
      }}>
        <Icon name={icon} size={19} />
      </div>
      <div style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink }}>{title}</div>
      {description && (
        <div style={{ fontSize: F.size.base, color: C.inkMuted, maxWidth: 320, lineHeight: 1.55 }}>{description}</div>
      )}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

// ─── Indicateur chiffré ───────────────────────────────────────────────────────
export function Stat({ label, value, unit, icon, tone = "neutral", trend, hint }) {
  const tones = {
    neutral: C.ink, navy: C.navy, success: C.success,
    danger: C.danger, warning: C.warning, info: C.info, accent: C.amber,
  };
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.lg,
      padding: "13px 15px", minWidth: 0,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 7,
        fontSize: F.size.sm, fontWeight: F.weight.medium, color: C.inkMuted,
      }}>
        {icon && <Icon name={icon} size={13} />}
        <span className="truncate">{label}</span>
      </div>
      <div style={{
        display: "flex", alignItems: "baseline", gap: 5,
        fontSize: F.size["2xl"], fontWeight: F.weight.bold,
        color: tones[tone] || C.ink, letterSpacing: "-.02em", ...numeric,
      }}>
        {value}
        {unit && <span style={{ fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkMuted }}>{unit}</span>}
      </div>
      {(trend || hint) && (
        <div style={{
          display: "flex", alignItems: "center", gap: 4, marginTop: 5,
          fontSize: F.size.sm,
          color: trend ? (trend > 0 ? C.success : trend < 0 ? C.danger : C.inkMuted) : C.inkFaint,
        }}>
          {trend != null && trend !== 0 && <Icon name={trend > 0 ? "trendUp" : "trendDown"} size={12} />}
          {trend != null && trend !== 0 ? `${trend > 0 ? "+" : ""}${trend}%` : null}
          {hint && <span style={{ color: C.inkFaint }}>{hint}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Référence de commande (monospace) ────────────────────────────────────────
export function Ref({ children, style }) {
  return <span className="mono" style={{ ...monoRef, color: C.ink, ...style }}>{children}</span>;
}

// ─── Fenêtre modale ───────────────────────────────────────────────────────────
export function Modal({ open = true, title, icon, onClose, children, footer, width = 460 }) {
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape" && onClose) onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={e => { if (e.target === e.currentTarget && onClose) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(10,37,64,.42)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, animation: "fadeIn .15s ease",
      }}
    >
      <div className="animate-scaleIn" style={{
        background: C.surface, borderRadius: R.xl, width: "100%", maxWidth: width,
        maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: SH.pop, overflow: "hidden",
      }}>
        {title && (
          <div style={{
            display: "flex", alignItems: "center", gap: 9,
            padding: "14px 16px", borderBottom: `1px solid ${C.border}`,
          }}>
            {icon && <Icon name={icon} size={17} color={C.inkMuted} />}
            <h3 style={{ flex: 1, fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink }}>{title}</h3>
            {onClose && <IconBtn icon="close" onClick={onClose} title="Fermer" />}
          </div>
        )}
        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && (
          <div style={{
            display: "flex", justifyContent: "flex-end", gap: 8,
            padding: "12px 16px", borderTop: `1px solid ${C.border}`, background: C.surfaceAlt,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Code QR ──────────────────────────────────────────────────────────────────
export function QRCanvas({ orderId, size = 150 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (canvasRef.current && orderId) {
      QRCode.toCanvas(canvasRef.current, trackUrl(orderId), {
        width: size, margin: 1,
        color: { dark: C.navy, light: "#ffffff" },
      });
    }
  }, [orderId, size]);
  return <canvas ref={canvasRef} />;
}

// ─── Notification ─────────────────────────────────────────────────────────────
export function Toast({ message, type = "success", onClose }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 250);
    }, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const conf = {
    success: { icon: "success", color: C.success, bg: C.successSoft, border: C.successBorder },
    error:   { icon: "error",   color: C.danger,  bg: C.dangerSoft,  border: C.dangerBorder },
    info:    { icon: "info",    color: C.info,    bg: C.infoSoft,    border: C.infoBorder },
    warning: { icon: "warning", color: C.warning, bg: C.warningSoft, border: C.warningBorder },
  }[type] || { icon: "info", color: C.info, bg: C.infoSoft, border: C.infoBorder };

  return (
    <div
      role="status"
      className={visible ? "toast-slide-in" : "toast-slide-out"}
      style={{
        position: "fixed", top: 14, left: "50%", transform: "translateX(-50%)",
        zIndex: 99999, maxWidth: 400, width: "calc(100% - 28px)",
        background: conf.bg, border: `1px solid ${conf.border}`, borderRadius: R.lg,
        padding: "11px 13px", display: "flex", alignItems: "center", gap: 10,
        boxShadow: SH.lg,
      }}
    >
      <Icon name={conf.icon} size={17} color={conf.color} />
      <span style={{ flex: 1, fontSize: F.size.md, fontWeight: F.weight.medium, color: C.ink }}>{message}</span>
      <IconBtn icon="close" title="Fermer" onClick={() => { setVisible(false); setTimeout(onClose, 250); }} />
    </div>
  );
}

// ─── Bandeau d'information ────────────────────────────────────────────────────
export function Callout({ tone = "info", icon, title, children, style }) {
  const tones = {
    info:    { color: C.info,    bg: C.infoSoft,    border: C.infoBorder,    icon: "info" },
    success: { color: C.success, bg: C.successSoft, border: C.successBorder, icon: "success" },
    warning: { color: C.warning, bg: C.warningSoft, border: C.warningBorder, icon: "warning" },
    danger:  { color: C.danger,  bg: C.dangerSoft,  border: C.dangerBorder,  icon: "error" },
    neutral: { color: C.inkMuted,bg: C.surfaceAlt,  border: C.border,        icon: "info" },
  };
  const t = tones[tone] || tones.info;
  return (
    <div style={{
      display: "flex", gap: 9, alignItems: "flex-start",
      background: t.bg, border: `1px solid ${t.border}`, borderRadius: R.md,
      padding: "10px 12px", ...style,
    }}>
      <span style={{ color: t.color, marginTop: 1 }}><Icon name={icon || t.icon} size={15} /></span>
      <div style={{ flex: 1, minWidth: 0, fontSize: F.size.base, color: C.inkBody, lineHeight: 1.55 }}>
        {title && <div style={{ fontWeight: F.weight.semi, color: C.ink, marginBottom: 2 }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

// ─── Navigation basse (mobile) ────────────────────────────────────────────────
export function BottomNav({ view, setView }) {
  const items = [
    { id: "landing",      icon: "home",   label: "Accueil" },
    { id: "order",        icon: "order",  label: "Commander" },
    { id: "track",        icon: "track",  label: "Suivi" },
    { id: "client_space", icon: "user",   label: "Profil" },
  ];

  return (
    <nav className="bottom-nav">
      {items.map(item => {
        const active = view === item.id;
        return (
          <button
            key={item.id}
            className={`bottom-nav-item ${active ? "active" : ""}`}
            onClick={() => setView(item.id)}
            aria-current={active ? "page" : undefined}
          >
            <Icon name={item.icon} size={19} strokeWidth={active ? 2 : 1.5} />
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── Compteur animé ───────────────────────────────────────────────────────────
export function AnimatedNumber({ value, duration = 900 }) {
  const [display, setDisplay] = useState(0);
  const numVal = parseInt(value) || 0;

  useEffect(() => {
    if (numVal === 0) { setDisplay(0); return; }
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setDisplay(numVal); return; }
    let start = 0;
    const increment = numVal / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= numVal) { setDisplay(numVal); clearInterval(timer); }
      else setDisplay(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [numVal, duration]);

  return <span style={numeric}>{display.toLocaleString("fr-FR")}</span>;
}

// ─── Marque de succès animée ──────────────────────────────────────────────────
// Remplace l'ancienne pluie de confettis : une confirmation nette et sobre.
export function Confetti() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "4px 0 12px" }} aria-hidden="true">
      <svg width="52" height="52" viewBox="0 0 52 52">
        <circle cx="26" cy="26" r="24" fill={C.successSoft} stroke={C.successBorder} strokeWidth="1.5" />
        <path
          d="M16 26.5 L23 33.5 L36 20"
          fill="none" stroke={C.success} strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
          strokeDasharray="48"
          style={{ animation: "drawCheck .45s cubic-bezier(.65,0,.45,1) .1s both" }}
        />
      </svg>
    </div>
  );
}

// ─── Chronologie de suivi ─────────────────────────────────────────────────────
export function StatusTimeline({ statuses, currentStatus, statusTimes }) {
  const idx = statuses.indexOf(currentStatus);
  const icons = {
    "En attente": "pending", "Collecté": "collect", "Réceptionné": "package",
    "En traitement": "washing", "Prêt": "clean", "Livré": "delivery", "Récupéré": "check",
  };

  return (
    <div style={{ padding: "4px 0" }}>
      {statuses.map((s, i) => {
        const isCompleted = i < idx;
        const isActive = i === idx;
        return (
          <div key={s} className="timeline-item">
            <div className="timeline-line" style={{ background: isCompleted ? C.successBorder : C.border }} />
            <div className={`timeline-dot ${isCompleted ? "completed" : isActive ? "active" : "pending"}`}>
              <Icon name={isCompleted ? "check" : icons[s] || "dot"} size={14} strokeWidth={isActive ? 2 : 1.5} />
            </div>
            <div style={{ flex: 1, paddingTop: 4, minWidth: 0 }}>
              <div style={{
                fontWeight: isActive ? F.weight.semi : F.weight.medium,
                fontSize: F.size.md,
                color: isActive ? C.ink : isCompleted ? C.inkBody : C.inkFaint,
              }}>
                {s}
              </div>
              {statusTimes && statusTimes[s] && (
                <div style={{ fontSize: F.size.sm, color: C.inkMuted, marginTop: 1, ...numeric }}>
                  {statusTimes[s]}
                </div>
              )}
              {isActive && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 5, marginTop: 5,
                  fontSize: F.size.sm, color: C.navy, fontWeight: F.weight.medium,
                }}>
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%", background: C.navy,
                    animation: "pulse 1.6s ease infinite",
                  }} />
                  En cours
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Programme de fidélité ────────────────────────────────────────────────────
export function LoyaltyProgress({ points, totalOrders, config }) {
  if (!config || !config.enabled) return null;
  const sorted = [...(config.thresholds || [])].sort((a, b) => a.points - b.points);
  const pts = points || 0;
  const nextThreshold = sorted.find(t => t.points > pts);
  const reached = sorted.filter(t => t.points <= pts).pop()?.points || 0;
  const progress = nextThreshold
    ? ((pts - reached) / (nextThreshold.points - reached)) * 100
    : 100;

  return (
    <div>
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        marginBottom: 7, gap: 10,
      }}>
        <span style={{ fontSize: F.size.base, color: C.inkMuted }}>
          <strong style={{ color: C.ink, fontSize: F.size.lg, ...numeric }}>{pts}</strong> points
          {totalOrders != null && <span style={{ color: C.inkFaint }}> · {totalOrders} commande{totalOrders > 1 ? "s" : ""}</span>}
        </span>
        <span style={{ fontSize: F.size.sm, color: C.inkFaint, ...numeric }}>{Math.round(progress)} %</span>
      </div>

      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.pill, height: 7, overflow: "hidden" }}>
        <div style={{
          background: C.navy, height: "100%", borderRadius: R.pill,
          width: `${Math.min(100, Math.max(0, progress))}%`,
          animation: "progressFill .6s ease both",
          transition: "width .4s ease",
        }} />
      </div>

      {nextThreshold ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, marginTop: 10,
          background: C.amberSoft, border: `1px solid ${C.amberBorder}`,
          borderRadius: R.md, padding: "9px 11px",
        }}>
          <Icon name="gift" size={16} color={C.amber} />
          <div style={{ fontSize: F.size.base, color: C.inkBody }}>
            Encore <strong style={{ color: C.ink, ...numeric }}>{nextThreshold.points - pts}</strong> points
            pour <strong style={{ color: C.amber }}>−{nextThreshold.discount} %</strong>
          </div>
        </div>
      ) : (
        <div style={{
          display: "flex", alignItems: "center", gap: 8, marginTop: 10,
          background: C.successSoft, border: `1px solid ${C.successBorder}`,
          borderRadius: R.md, padding: "9px 11px",
          fontSize: F.size.base, color: C.success, fontWeight: F.weight.medium,
        }}>
          <Icon name="trophy" size={16} />
          Palier maximum atteint
        </div>
      )}
    </div>
  );
}

export { Icon };
